/** /prep-lists/$id/print: the print preview for a day list or a weekly plan
 *  (docs/parity/prep-lists.md §4.4, §4.7, §6). `$id_` keeps it out of the
 *  editor's layout: the editor and the print view are two full pages of the
 *  same list. `?section=N` prints one section (the section's own Print,
 *  which saves first). It prints what is saved, never an unsaved draft. */
import { createFileRoute } from '@tanstack/react-router';
import { usePrepListPrint } from '@/api/queries/prep-lists';
import { ComingSoon, useFeatureGate } from '@/components/ComingSoon';
import { isNotFound, ListLoadFailed, ListLoading, ListNotFound } from '@/features/prep-lists/EditorStates';
import { PrintPreview } from '@/features/prep-lists/PrintPreview';

type Search = { section?: number };

export const Route = createFileRoute('/_app/prep-lists/$id_/print')({
  validateSearch: (s: Record<string, unknown>): Search => {
    const n = Number(s.section);
    return { section: s.section !== undefined && Number.isInteger(n) && n >= 0 ? n : undefined };
  },
  component: PrepListPrintRoute,
});

function PrepListPrintRoute() {
  if (useFeatureGate('prep')) return <ComingSoon feature="prep" />;
  return <Print />;
}

function Print() {
  const { id } = Route.useParams();
  const { section } = Route.useSearch();
  const prepListId = Number(id);
  const valid = Number.isInteger(prepListId) && prepListId > 0;
  const { data, isError, error, refetch, isFetching } = usePrepListPrint(valid ? prepListId : undefined);

  if (!valid) return <ListNotFound />;
  if (!data) {
    if (isError) return isNotFound(error) ? <ListNotFound /> : <ListLoadFailed onRetry={() => void refetch()} retrying={isFetching} />;
    return <ListLoading />;
  }
  return <PrintPreview data={data} sectionIndex={section} />;
}
